import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { VendorSection } from './VendorSection.jsx';
import { SensorPanel } from './SensorPanel.jsx';
import { TempChart } from './TempChart.jsx';
import { DocumentLinker } from './DocumentLinker.jsx';
import { todayStr } from '../lib/format.js';

export function EquipmentDetail({ equipment: initialEq, vendors, locationId, userId, isManager = true, onBack, onArchived, onDeleted }) {
  const [eq, setEq] = React.useState(initialEq);
  const [temps, setTemps] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [showLogTemp, setShowLogTemp] = React.useState(false);
  const [tempInput, setTempInput] = React.useState('');
  const [tempPeriod, setTempPeriod] = React.useState('Opening');
  const [showAddPart, setShowAddPart] = React.useState(false);
  const [partForm, setPartForm] = React.useState({ part_name:'', part_number:'', last_replaced:'', replacement_interval_days:'', notes:'' });
  const [showAddService, setShowAddService] = React.useState(false);
  const [serviceForm, setServiceForm] = React.useState({ service_date: todayStr(), description:'', vendor_id:'', cost:'', next_service_date:'' });
  const [saving, setSaving] = React.useState(false);

  async function handleArchive() {
    if (!confirm('Archive this equipment? It will be hidden from the list but can be restored later.')) return;
    await api('PUT', `/api/equipment/${eq.id}/archive`);
    (onArchived || onBack)();
  }
  async function handleDelete() {
    if (!confirm(`Permanently delete "${eq.name}" and ALL its temperature logs, parts, and service history? This cannot be undone.`)) return;
    await api('DELETE', `/api/equipment/${eq.id}`);
    (onDeleted || onBack)();
  }

  async function load() {
    const [t, s, allEq] = await Promise.all([
      api('GET', `/api/equipment/${initialEq.id}/temperatures?days=30`),
      api('GET', `/api/equipment/${initialEq.id}/service-history`),
      api('GET', `/api/equipment?location_id=${locationId}`),
    ]);
    setTemps(Array.isArray(t) ? t : []);
    setServices(Array.isArray(s) ? s : []);
    const updated = (Array.isArray(allEq) ? allEq : []).find(e => e.id === initialEq.id);
    if (updated) setEq(updated);
  }
  React.useEffect(() => { load(); }, [initialEq.id]);

  async function handleLogTemp(e) {
    e.preventDefault(); if (!tempInput) return; setSaving(true);
    try {
      await api('POST', `/api/equipment/${eq.id}/temperature`, { temperature: parseFloat(tempInput), period: tempPeriod, logged_by: userId, location_id: locationId });
      setShowLogTemp(false); setTempInput('');
      const t = await api('GET', `/api/equipment/${eq.id}/temperatures?days=30`);
      setTemps(Array.isArray(t) ? t : []);
    } finally { setSaving(false); }
  }
  async function handleAddPart(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api('POST', `/api/equipment/${eq.id}/parts`, partForm);
      setShowAddPart(false); setPartForm({ part_name:'', part_number:'', last_replaced:'', replacement_interval_days:'', notes:'' });
      load();
    } finally { setSaving(false); }
  }
  async function handleAddService(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api('POST', `/api/equipment/${eq.id}/service`, serviceForm);
      setShowAddService(false); setServiceForm({ service_date: todayStr(), description:'', vendor_id:'', cost:'', next_service_date:'' });
      const s = await api('GET', `/api/equipment/${eq.id}/service-history`);
      setServices(Array.isArray(s) ? s : []);
    } finally { setSaving(false); }
  }
  async function handleMarkReplaced(partId) {
    await api('PUT', `/api/equipment/parts/${partId}`, { last_replaced: todayStr() });
    load();
  }

  const latestTemp = temps.length > 0 ? temps[temps.length - 1] : null;
  const tempStatus = !latestTemp ? 'none' : latestTemp.temperature > 41 ? 'danger' : latestTemp.temperature > 38 ? 'warning' : 'ok';
  const parts = eq.parts || [];
  const linkedVendors = eq.vendors || [];

  return (
    <div className="fade-in">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1">← Back to Equipment</button>
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-ink">{eq.name}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{eq.type}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {latestTemp && eq.requires_temp_log ? (
              <div className={`text-center px-4 py-2 rounded-xl font-bold ${tempStatus==='danger'?'bg-red-100 text-red-700':tempStatus==='warning'?'bg-yellow-100 text-yellow-700':'bg-green-100 text-green-700'}`}>
                <p className="text-2xl">{latestTemp.temperature}{eq.measurement_unit||'°F'}</p>
                <p className="text-xs font-normal">{tempStatus==='danger'?'⚠ Too warm':tempStatus==='warning'?'⚠ Watch':'✓ Good'}</p>
              </div>
            ) : null}
            {isManager && (
              <div className="flex gap-2">
                <button onClick={handleArchive} className="px-3 py-1.5 bg-amber-50 text-amber-700 text-xs font-semibold rounded-lg hover:bg-amber-100 transition-all">Archive</button>
                <button onClick={handleDelete} className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-100 transition-all">Delete</button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          {eq.serial_number && <div><span className="text-gray-400 block">Serial #</span><span className="text-ink font-medium">{eq.serial_number}</span></div>}
          {eq.model && <div><span className="text-gray-400 block">Model</span><span className="text-ink font-medium">{eq.model}</span></div>}
          {eq.install_date && <div><span className="text-gray-400 block">Installed</span><span className="text-ink font-medium">{eq.install_date}</span></div>}
        </div>
      </div>

      {/* Vendors */}
      <VendorSection equipment={eq} allVendors={vendors} onLinked={load} onUnlinked={load} />

      {/* MOCREO sensor panel — manager+ only. Shown for every piece of
          equipment so the manager can opt-in by linking a probe; the
          panel collapses gracefully when nothing's linked. */}
      {isManager && (
        <SensorPanel equipment={eq} currentUser={{ id: userId }} onChanged={load} />
      )}

      {/* Temperature — shown when requires_temp_log OR a sensor is linked */}
      {(eq.requires_temp_log || eq.sensor_id) ? (
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-ink">Temperature Log</h3>
          <button onClick={() => setShowLogTemp(s => !s)}
            className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-lg hover:bg-blue-100 transition-all">
            🌡 Log Reading
          </button>
        </div>
        {showLogTemp && (
          <form onSubmit={handleLogTemp} className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg flex-wrap">
            <input type="number" step="0.1" value={tempInput} onChange={e => setTempInput(e.target.value)} required
              className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none" placeholder={eq.measurement_unit||'°F'} autoFocus/>
            <select value={tempPeriod} onChange={e => setTempPeriod(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-ink bg-white focus:outline-none">
              {['Opening','Midday','Closing'].map(p=><option key={p}>{p}</option>)}
            </select>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50">{saving?'...':'Save'}</button>
            <button type="button" onClick={() => setShowLogTemp(false)} className="text-gray-400 hover:text-gray-600 text-sm">Cancel</button>
          </form>
        )}
        {temps.length >= 2 && <div className="mb-3"><TempChart readings={temps.slice(-200)} equipment={eq} /></div>}
        <div className="flex gap-4 text-xs text-gray-500 mb-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-400 rounded-full inline-block"></span>35–38{eq.measurement_unit||'°F'} Safe</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-400 rounded-full inline-block"></span>38–41{eq.measurement_unit||'°F'} Watch</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded-full inline-block"></span>&gt;41{eq.measurement_unit||'°F'} Danger</span>
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {temps.slice().reverse().slice(0,20).map((t,i) => (
            <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50 last:border-0">
              <span className="text-gray-500">{new Date(t.logged_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</span>
              <span className="text-gray-400">{t.period}</span>
              <span className={`font-semibold px-2 py-0.5 rounded-full ${t.temperature>41?'bg-red-100 text-red-700':t.temperature>38?'bg-yellow-100 text-yellow-700':'bg-green-100 text-green-700'}`}>{t.temperature}{eq.measurement_unit||'°F'}</span>
            </div>
          ))}
          {temps.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No readings yet</p>}
        </div>
      </div>
      ) : null}

      {/* Parts */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-ink">Parts & Maintenance</h3>
          <button onClick={() => setShowAddPart(s=>!s)}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-all">+ Add Part</button>
        </div>
        {showAddPart && (
          <form onSubmit={handleAddPart} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3 fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={partForm.part_name} onChange={e=>setPartForm(f=>({...f,part_name:e.target.value}))} required
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Part name *"/>
              <input value={partForm.part_number} onChange={e=>setPartForm(f=>({...f,part_number:e.target.value}))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Part number"/>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Last replaced</label>
                <input type="date" value={partForm.last_replaced} onChange={e=>setPartForm(f=>({...f,last_replaced:e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"/>
              </div>
              <input type="number" value={partForm.replacement_interval_days} onChange={e=>setPartForm(f=>({...f,replacement_interval_days:e.target.value}))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Replace every (days)"/>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowAddPart(false)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark disabled:opacity-50">{saving?'Saving...':'Add Part'}</button>
            </div>
          </form>
        )}
        {parts.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No parts tracked yet</p> : (
          <div className="space-y-2">
            {parts.map(part => {
              const daysUntil = part.last_replaced && part.replacement_interval_days
                ? Math.round((new Date(part.last_replaced) - new Date()) / 86400000) + Number(part.replacement_interval_days)
                : null;
              return (
                <div key={part.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink">{part.part_name}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      {part.part_number && <span>P/N: {part.part_number}</span>}
                      {part.last_replaced && <span>Last: {part.last_replaced}</span>}
                      {daysUntil !== null && (
                        <span className={daysUntil<0?'text-red-600 font-semibold':daysUntil<30?'text-yellow-600 font-semibold':'text-gray-500'}>
                          {daysUntil<0?`${Math.abs(daysUntil)}d overdue`:`${daysUntil}d until next`}
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => handleMarkReplaced(part.id)}
                    className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-lg hover:bg-green-100 transition-all flex-shrink-0">✓ Replaced</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Linked documents — cleaning SOPs, manuals, etc. shared with the
          Knowledge Hub and the task list (one upload, many places).
          Staff see the linked PDFs read-only; only managers can change links. */}
      <DocumentLinker scope="equipment" id={eq.id} canEdit={isManager} userId={userId} />

      {/* Service history */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-ink">Service History</h3>
          <button onClick={() => setShowAddService(s=>!s)}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-all">+ Log Service</button>
        </div>
        {showAddService && (
          <form onSubmit={handleAddService} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3 fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Service date *</label>
                <input type="date" value={serviceForm.service_date} onChange={e=>setServiceForm(f=>({...f,service_date:e.target.value}))} required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"/>
              </div>
              <select value={serviceForm.vendor_id} onChange={e=>setServiceForm(f=>({...f,vendor_id:e.target.value}))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                <option value="">No vendor</option>
                {vendors.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <textarea value={serviceForm.description} onChange={e=>setServiceForm(f=>({...f,description:e.target.value}))} required
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
              placeholder="Service description *" rows={2}/>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="number" step="0.01" value={serviceForm.cost} onChange={e=>setServiceForm(f=>({...f,cost:e.target.value}))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" placeholder="Cost ($)"/>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Next service date</label>
                <input type="date" value={serviceForm.next_service_date} onChange={e=>setServiceForm(f=>({...f,next_service_date:e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"/>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowAddService(false)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark disabled:opacity-50">{saving?'Saving...':'Log Service'}</button>
            </div>
          </form>
        )}
        {services.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No service visits recorded</p> : (
          <div className="space-y-3">
            {services.map(s => (
              <div key={s.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <p className="text-sm font-semibold text-ink">{s.service_date}</p>
                  {s.vendor_name && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{s.vendor_name}</span>}
                  {s.cost && <span className="text-xs text-gray-500">${Number(s.cost).toFixed(2)}</span>}
                </div>
                <p className="text-sm text-gray-700">{s.description}</p>
                {s.next_service_date && <p className="text-xs text-gray-400 mt-1">Next service: {s.next_service_date}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
